// Portfolio Administration v1.0 PR 5 — Overview tab on the Local
// Enforcer registry detail page. Per Entity Detail Page Layout v1.1
// §4.1 the Overview tab renders identity + coverage + fee structure +
// ownership only. The richer per-tab data lives on its own tab.

import { Card } from "@/design-system/components/ui";
import type { LocalEnforcerDetailDto } from "@/api/portfolioAdmin/pr4bTypes";
import { useDateFormatter } from "@/utils/datetime";

function parseJsonArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function EnforcerOverviewTab({
  enforcer,
}: {
  enforcer: LocalEnforcerDetailDto;
}) {
  const { date } = useDateFormatter();
  const jurisdictions = parseJsonArray(enforcer.jurisdictionsServedJson);
  const practiceAreas = parseJsonArray(enforcer.practiceAreasJson);
  const feeStructures = parseJsonArray(enforcer.feeStructureTypesJson);
  const mailing = [
    enforcer.mailingLine1,
    enforcer.mailingLine2,
    [enforcer.mailingCity, enforcer.mailingState, enforcer.mailingZip]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-space-3"
      data-testid="enforcer-overview-tab"
    >
      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Identity</h3>
        <dl className="grid grid-cols-[160px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Legal entity</dt>
          <dd className="m-0">{enforcer.legalEntityName}</dd>
          <dt className="text-gray-mid">DBA</dt>
          <dd className="m-0">{enforcer.dbaName ?? "—"}</dd>
          <dt className="text-gray-mid">Type</dt>
          <dd className="m-0">{enforcer.enforcerType}</dd>
          <dt className="text-gray-mid">Onboarded</dt>
          <dd className="m-0">
            {enforcer.onboardedDate ? date(enforcer.onboardedDate) : "—"}
          </dd>
          <dt className="text-gray-mid">E1 sponsor</dt>
          <dd className="m-0">{enforcer.assignedE1OwnerId ?? "—"}</dd>
        </dl>
      </Card>
      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Primary contact</h3>
        <dl className="grid grid-cols-[160px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Name</dt>
          <dd className="m-0">{enforcer.primaryContactName ?? "—"}</dd>
          <dt className="text-gray-mid">Title</dt>
          <dd className="m-0">{enforcer.primaryContactTitle ?? "—"}</dd>
          <dt className="text-gray-mid">Email</dt>
          <dd className="m-0">{enforcer.primaryContactEmail ?? "—"}</dd>
          <dt className="text-gray-mid">Phone</dt>
          <dd className="m-0">{enforcer.primaryContactPhone ?? "—"}</dd>
          <dt className="text-gray-mid">Mobile</dt>
          <dd className="m-0">{enforcer.primaryContactMobile ?? "—"}</dd>
          <dt className="text-gray-mid">After-hours</dt>
          <dd className="m-0">
            {enforcer.afterHoursContactName
              ? `${enforcer.afterHoursContactName} — ${enforcer.afterHoursContactPhone ?? ""}`
              : "—"}
          </dd>
        </dl>
      </Card>
      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Coverage</h3>
        <dl className="grid grid-cols-[160px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Mailing address</dt>
          <dd className="m-0">{mailing || "—"}</dd>
          <dt className="text-gray-mid">Jurisdictions</dt>
          <dd className="m-0">
            {jurisdictions.length === 0 ? (
              "—"
            ) : (
              <div className="flex flex-wrap gap-space-1">
                {jurisdictions.map((j) => (
                  <span
                    key={j}
                    className="px-space-1 py-[1px] text-[10px] uppercase tracking-[0.16em] font-display font-bold rounded-sm bg-onyx text-white-pure"
                    data-testid={`enforcer-jurisdiction-${j}`}
                  >
                    {j}
                  </span>
                ))}
              </div>
            )}
          </dd>
          <dt className="text-gray-mid">Practice areas</dt>
          <dd className="m-0">
            {practiceAreas.length === 0 ? "—" : practiceAreas.join(" · ")}
          </dd>
        </dl>
      </Card>
      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Fee structure</h3>
        <dl className="grid grid-cols-[160px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Supported</dt>
          <dd className="m-0">
            {feeStructures.length === 0 ? "—" : feeStructures.join(" · ")}
          </dd>
          <dt className="text-gray-mid">Typical hourly</dt>
          <dd className="m-0">
            {enforcer.typicalHourlyRangeLow != null &&
            enforcer.typicalHourlyRangeHigh != null
              ? `$${enforcer.typicalHourlyRangeLow}–$${enforcer.typicalHourlyRangeHigh}/hr`
              : "—"}
          </dd>
          <dt className="text-gray-mid">Retainer required</dt>
          <dd className="m-0">{enforcer.retainerRequired ? "Yes" : "No"}</dd>
          <dt className="text-gray-mid">Retainer amount</dt>
          <dd className="m-0">
            {enforcer.retainerAmount != null
              ? `$${enforcer.retainerAmount.toLocaleString()}`
              : "—"}
          </dd>
        </dl>
      </Card>
    </div>
  );
}
